import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';

import { IAuthContextValue, IAuthState } from '@comet/components/auth/types';
import {
  getCurrentUser,
  initiateAmplify,
  signIn,
  signOut,
} from '@comet/amplify';

interface AuthProviderProps {
  children: ReactNode;
}

const initialAuthState: IAuthState = {
  user: null,
  loading: true,
  error: null,
};

const AuthContext = createContext<IAuthContextValue>({
  ...initialAuthState,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
});

const useAuth = () => useContext(AuthContext);

const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState(initialAuthState.user);
  const [error, setError] = useState(initialAuthState.error);
  const [loading, setLoading] = useState(initialAuthState.loading);

  const login = async (username: string, password: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const user = await signIn(username, password);

      setUser({
        username: user.username,
        email: user.attributes.email,
        name: user.attributes.name,
        id: user.attributes.sub,
        // TODO
        role: 'EDITOR',
      });
    } catch (error: any) {
      setError({
        type: 'login',
        code: error.code,
      });
    }

    setLoading(false);
  };

  const logout = async (): Promise<void> => {
    try {
      await signOut();
    } catch (error: any) {
      setError({
        type: 'logout',
        code: error.code,
      });
    }

    setUser(null);
  };

  useEffect(() => {
    const setCurrentUser = async () => {
      setLoading(true);

      try {
        const user = await getCurrentUser();

        setUser({
          username: user.username,
          email: user.attributes.email,
          name: user.attributes.name,
          id: user.attributes.sub,
          // TODO
          role: 'EDITOR',
        });
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initiateAmplify();

    setCurrentUser();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { AuthProvider, useAuth };
